import { MenuSelectionWizard, useSelection } from '@order-up/client/vite';
import type { IMenuGroup } from '@order-up/models';
import React, { useState } from 'react';
import { Route, BrowserRouter as Router, Routes } from 'react-router-dom';
import { ClientProviders } from './components/ClientProviders';
import { MenuDisplay } from './components/menu/MenuDisplay';

// Inner app content that uses the selection context
function AppContent(): React.JSX.Element {
  const { hasValidSelection } = useSelection();
  const [selectedMenuGroup, setSelectedMenuGroup] = useState<IMenuGroup | null>(null);
  const [hasCompletedMenuSelection, setHasCompletedMenuSelection] = useState(false);
  const [prevHasValidSelection, setPrevHasValidSelection] = useState(hasValidSelection);

  // Reset menu selection when tenant/restaurant changes — React-recommended
  // "adjust state during render" pattern instead of an effect cascade.
  if (hasValidSelection !== prevHasValidSelection) {
    setPrevHasValidSelection(hasValidSelection);
    if (hasValidSelection) {
      setSelectedMenuGroup(null);
      setHasCompletedMenuSelection(false);
    }
  }

  if (!hasValidSelection) {
    return (
      <div className="menu-error">
        <div className="menu-error__content">
          <h1 className="menu-error__title">Setup Required</h1>
          <p className="menu-error__message">
            Please complete restaurant selection to view the menu.
          </p>
        </div>
      </div>
    );
  }

  // Show menu selection wizard if no menu has been selected yet
  if (!hasCompletedMenuSelection) {
    return (
      <MenuSelectionWizard
        onMenuGroupSelect={(menuGroup: IMenuGroup | null) => {
          setSelectedMenuGroup(menuGroup);
          setHasCompletedMenuSelection(true);
        }}
      />
    );
  }

  // Show the actual menu display
  return (
    <Routes>
      <Route
        path="/tv"
        element={
          <MenuDisplay
            selectedMenuGroup={selectedMenuGroup}
            onBackToMenuSelection={() => {
              setSelectedMenuGroup(null);
              setHasCompletedMenuSelection(false);
            }}
          />
        }
      />
      <Route
        path="/"
        element={
          <MenuDisplay
            selectedMenuGroup={selectedMenuGroup}
            onBackToMenuSelection={() => {
              setSelectedMenuGroup(null);
              setHasCompletedMenuSelection(false);
            }}
          />
        }
      />
    </Routes>
  );
}

function App(): React.JSX.Element {
  return (
    <ClientProviders>
      <Router>
        <AppContent />
      </Router>
    </ClientProviders>
  )
}

export default App;
